/**
 * Inception's reading voice, defined once.
 *
 * Every word the article narrator says (the article, its fixed lines, the
 * answers to questions) is rendered by this voice, so the brand sounds like
 * one reader on every page and in every language. Change the voice HERE,
 * never per call: the model, the voice and the delivery note together make
 * the voice, and all three are part of every cached clip's identity, so a
 * change here re-renders clips as they are next needed instead of mixing
 * two voices in one article.
 *
 * Chosen 2026-10-23 (docs/ARTICLE_NARRATOR.md): Gemini 3.8 Flash TTS, the
 * prebuilt voice Sulafat (Google's "warm"), with a conversational delivery
 * note. A voice designed for us in Google's Voice design ("Inception reader",
 * voice_fiwktvpzm83k) was auditioned beside it and not preferred; swapping
 * it in is this one constant.
 *
 * Isomorphic and secret-free: the browser computes the same clip paths as
 * the server, so a clip already rendered is fetched straight from storage.
 */
import { LanguageCode, type LanguageType } from 'shared/src/types/languages.type';

export const NARRATOR_VOICE = {
  model: 'gemini-3.8-flash-tts',
  voice: 'Sulafat',
  style:
    'Relaxed and conversational, as if reading an interesting article aloud to a friend across the kitchen table. ' +
    'Natural pauses and varied intonation, a little smile, never presenting or announcing.',
  /** Bump to re-render every clip without changing the voice or the note. */
  version: 1,
} as const;

/** Languages the hosted voice reads (Gemini 3.8 Flash TTS's table, 2026-10-23).
 *  The rest (Welsh, Irish, Gaelic, Romansh, Māori) use the device's own voice. */
const HOSTED: LanguageType[] = [
  LanguageCode.english, LanguageCode.french, LanguageCode.spanish, LanguageCode.german, LanguageCode.italian,
  LanguageCode.portugueseBR, LanguageCode.portuguesePT, LanguageCode.dutch, LanguageCode.polish, LanguageCode.swedish,
  LanguageCode.hindi, LanguageCode.catalan, LanguageCode.galician, LanguageCode.basque, LanguageCode.tamil,
  LanguageCode.telugu, LanguageCode.bengali, LanguageCode.marathi, LanguageCode.japanese,
];
export const HOSTED_VOICE_LANGUAGES: ReadonlySet<LanguageType> = new Set<LanguageType>(HOSTED);

/** Where one variety of a language must not drift into the other. */
const PRONUNCIATION: Partial<Record<LanguageType, string>> = {
  [LanguageCode.portugueseBR]: 'Brazilian Portuguese pronunciation.',
  [LanguageCode.portuguesePT]: 'European Portuguese pronunciation, as spoken in Portugal.',
};

export function narratorStyle(lang: LanguageType): string {
  return [NARRATOR_VOICE.style, PRONUNCIATION[lang]].filter(Boolean).join(' ');
}

/** Public bucket of rendered clips, one MP3 per piece of text. */
export const NARRATOR_AUDIO_BUCKET = 'narrator-audio';

/** Fixed lines the reader says, by their key in article-ui-content. */
export const NARRATOR_LINE_KEYS = ['intro', 'backToArticle', 'end', 'answerFailed', 'searching', 'stillSearching'] as const;
export type NarratorLineKey = (typeof NARRATOR_LINE_KEYS)[number];

/** Everything a clip's sound depends on, as one string to hash. */
export function clipIdentity(lang: LanguageType, text: string): string {
  return [NARRATOR_VOICE.model, NARRATOR_VOICE.voice, `v${NARRATOR_VOICE.version}`, narratorStyle(lang), lang, text].join('\n');
}

/** Object path of a clip, from the SHA-256 (hex) of its identity. */
export function clipPathFromHash(lang: LanguageType, sha256Hex: string): string {
  return `${NARRATOR_VOICE.voice.toLowerCase()}-v${NARRATOR_VOICE.version}/${lang}/${sha256Hex.slice(0, 40)}.mp3`;
}

/** The clip's public URL under a Supabase project URL. */
export function publicClipUrl(supabaseUrl: string, path: string): string {
  return `${supabaseUrl.replace(/\/+$/, '')}/storage/v1/object/public/${NARRATOR_AUDIO_BUCKET}/${path}`;
}

/** Content type of a spoken answer from /api/articles/ask: one JSON line, then raw PCM. */
export const SPOKEN_ANSWER_CONTENT_TYPE = 'application/vnd.inception.spoken-answer';

/** The rendered voice's sample rate (24 kHz, mono, 16-bit). */
export const NARRATION_SAMPLE_RATE = 24_000;
